import { z } from "zod";

// TES-1256 (Ella 2.1 slice J) — F-05's dose questions answered from her dose
// log. Drive §5.6 says MONTH_LAST_DOSE_DATE and MONTH_ADHERENCE are asked
// "endast när dosloggen saknar tillräcklig information". This is what the
// server can say from `dose_events`, per question, and whether it is enough.
//
// WHEN THE LOG IS SUFFICIENT. Nothing is ever inferred from a missing entry:
// a passed planned day she never answered is "Ingen dos registrerad", never
// "missed" (TES-1151), so it cannot be counted either way.
//
//   MONTH_LAST_DOSE_DATE is answered by the log when her most recent taken
//   dose has a date (`takenOn`; the first dose's date for the first dose),
//   and no planned dose day after it has passed unanswered: she may have
//   taken that one without registering it.
//
//   MONTH_ADHERENCE ("sedan förra månadsavstämningen") is answered by the
//   log when the period from her previous F-05 (or, before her first F-05,
//   from her first dose) holds at least one passed planned dose day after
//   the first dose, and
//   every passed planned dose day in it is answered, taken or not taken.
//   Today's planned dose, while unanswered, has not passed yet and is not
//   counted. Days a doctor's pause covered, and days while she awaits the
//   doctor's restart date, are not planned days (treatment-overview.ts).
//   Zero not-taken answers is "alla", one is "missat_en", more is
//   "missat_flera". A dose taken on another day than planned counts as
//   taken, as does a dose she reported as a different strength: the
//   question asks how many doses she took, not whether they were on time.
//   The log never yields "osaker" or "inte_aktuellt".
//
// TES-1418 — F-04's WEEK_DOSE_ADHERENCE ("Har du tagit behandlingen enligt
// din nuvarande ordination sedan förra incheckningen?"; Drive 2.1 §5.6 shows
// it only when "dosdata inte redan besvarar frågan") is answered by the log
// on the same rules as MONTH_ADHERENCE, over the period since her previous
// check-in: her latest F-04 or F-05, since F-05 replaces F-04 the week it is
// due; before either, her first dose. One difference, because the question
// asks about the ordination and its own follow-up lists "Tog annan dag": a
// dose taken on another day than planned does not count as taken as
// prescribed. Every planned day taken on its day is "ja"; any "Jag tog
// ingen dos", or any dose taken on another day, is "nej". A taken dose
// without a date (`dose_date_unknown`) cannot say which, so the question is
// asked. The log never yields "inte_aktuellt" or "osaker".
//
// All of them need a once-a-week ordination: without planned dose days a dose
// after the first one has nowhere to be registered, so the log is silent
// about it rather than complete.
//
// Otherwise the question is asked as today, and `reason` says why.

export const DOSE_SUMMARY_INSUFFICIENT_REASONS = [
  // No treatment, or no first dose registered in Ella: there is no log.
  "no_dose_log",
  // Her ordination plans no weekly dose day (not once a week), so nothing
  // after the first dose can be registered against the log.
  "no_weekly_schedule",
  // No passed planned dose day after the first dose in the period: the
  // period is too young (the first dose alone says nothing about adherence).
  "no_planned_dose_in_period",
  // A passed planned dose day she never answered ("Ingen dos registrerad").
  "unregistered_dose",
  // Her latest taken dose has no date: answered before TES-1165 asked it.
  "dose_date_unknown",
] as const;
export const doseSummaryInsufficientReasonSchema = z.enum(
  DOSE_SUMMARY_INSUFFICIENT_REASONS,
);
export type DoseSummaryInsufficientReason = z.infer<
  typeof doseSummaryInsufficientReasonSchema
>;

/** MONTH_ADHERENCE's own option values that the log can produce. */
export const DOSE_SUMMARY_ADHERENCE_VALUES = [
  "alla",
  "missat_en",
  "missat_flera",
] as const;
export type DoseSummaryAdherence =
  (typeof DOSE_SUMMARY_ADHERENCE_VALUES)[number];

/** WEEK_DOSE_ADHERENCE's own option values that the log can produce. */
export const DOSE_SUMMARY_WEEK_ADHERENCE_VALUES = ["ja", "nej"] as const;
export type DoseSummaryWeekAdherence =
  (typeof DOSE_SUMMARY_WEEK_ADHERENCE_VALUES)[number];

/** Where F-04's period starts: her previous F-04 or F-05, or her first dose. */
export const DOSE_SUMMARY_WEEK_PERIOD_KINDS = [
  "last_weekly_checkin",
  "last_monthly_review",
  "first_dose",
] as const;
export type DoseSummaryWeekPeriodKind =
  (typeof DOSE_SUMMARY_WEEK_PERIOD_KINDS)[number];

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const doseLogSummarySchema = z.object({
  /** Stockholm date the summary was computed for. */
  today: isoDate,
  lastDoseDate: z.object({
    /** Her most recent taken dose's date, when the log is sufficient. */
    value: isoDate.nullable(),
    sufficient: z.boolean(),
    reason: doseSummaryInsufficientReasonSchema.nullable(),
  }),
  adherence: z.object({
    /** First day of the period: her previous F-05, else her first dose. */
    periodFrom: isoDate.nullable(),
    periodFromKind: z.enum(["last_monthly_review", "first_dose"]).nullable(),
    /** Passed planned dose days in the period, the first dose included. */
    planned: z.number().int().nonnegative(),
    taken: z.number().int().nonnegative(),
    notTaken: z.number().int().nonnegative(),
    notRegistered: z.number().int().nonnegative(),
    /** MONTH_ADHERENCE's option value, when the log is sufficient. */
    value: z.enum(DOSE_SUMMARY_ADHERENCE_VALUES).nullable(),
    sufficient: z.boolean(),
    reason: doseSummaryInsufficientReasonSchema.nullable(),
  }),
  /**
   * TES-1418 — F-04's WEEK_DOSE_ADHERENCE. Sent only to a build at or after
   * CLIENT_CONTRACT_CHANGES.f04DoseSummary; optional so either side still
   * parses a summary without it.
   */
  weekAdherence: z
    .object({
      /** First day of the period: her previous check-in, else her first dose. */
      periodFrom: isoDate.nullable(),
      periodFromKind: z.enum(DOSE_SUMMARY_WEEK_PERIOD_KINDS).nullable(),
      /** Passed planned dose days in the period, the first dose included. */
      planned: z.number().int().nonnegative(),
      taken: z.number().int().nonnegative(),
      /** Of `taken`: taken on another day than planned. */
      takenOtherDay: z.number().int().nonnegative(),
      notTaken: z.number().int().nonnegative(),
      notRegistered: z.number().int().nonnegative(),
      /** WEEK_DOSE_ADHERENCE's option value, when the log is sufficient. */
      value: z.enum(DOSE_SUMMARY_WEEK_ADHERENCE_VALUES).nullable(),
      sufficient: z.boolean(),
      reason: doseSummaryInsufficientReasonSchema.nullable(),
    })
    .optional(),
});
export type DoseLogSummary = z.infer<typeof doseLogSummarySchema>;
